import { useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InviteDialogProps {
  children: React.ReactNode;
}

export function InviteDialog({ children }: InviteDialogProps) {
  const [emails, setEmails] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const handleInvite = async () => {
    if (!emails.trim()) {
      toast({
        title: "Error",
        description: "Please enter at least one email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const emailList = emails
        .split(",")
        .map(email => email.trim())
        .filter(email => email && email.includes("@"));

      if (emailList.length === 0) {
        toast({
          title: "Error",
          description: "Please enter valid email addresses.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('invite-users', {
        body: { emails: emailList }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to invoke function');
      }

      const results = data?.results || [];
      const successCount = results.filter((r: any) => r.success).length;
      const failureCount = results.length - successCount;

      if (successCount > 0) {
        toast({
          title: "Success!",
          description: `Successfully sent ${successCount} invitation email(s).${failureCount > 0 ? ` ${failureCount} failed.` : ''}`,
        });
      } else {
        throw new Error('No invitations were sent successfully');
      }

      setEmails("");
      setOpen(false);
    } catch (error) {
      console.error('Error inviting users:', error);
      toast({
        title: "Error",
        description: "Failed to send invitations. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Users to Supplyfied Data Room
          </DialogTitle>
          <DialogDescription>
            Enter email addresses separated by commas to invite users to the data room.
            Accounts will be created automatically and invitation emails will be sent.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="emails">
              Email Addresses
            </Label>
            <Textarea
              id="emails"
              placeholder="user1@example.com, user2@example.com, user3@example.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className="min-h-[80px]"
            />
            <p className="text-sm text-muted-foreground">
              Separate multiple email addresses with commas
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending Invites...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Send Invites
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}